import React, {
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { Droppable } from "react-beautiful-dnd";
import { Tooltip } from "@mui/material";

import KanbanCard from "./KanbanCard";
import CreateCard from "../../pages/board/components/Kanban/CreateCard";
import { TooltipName } from "../../utils/Enums";
import { useCustomSearchParams } from "../../hooks/useCustomSearchParams";

function KanbanColumn({
  name,
  stage,
  projects,
  onDragEnter,
  onDragEnd,
  boardName,
  id,
  callback,
  maxHeight,
  isMyTaskView = false,
  boardId,
}) {
  const [mouseIsHovering, setMouseIsHovering] = useState(false);
  const [milestone] = useCustomSearchParams("milestone");
  const scrollRef = useRef(null);
  const [scrollToCardId, setScrollToCardId] = useState(null);

  const preventing = (e) => {
    e.preventDefault();
  };

  // Memoize the onDragEnd function if it's defined in this component
  const memoizedOnDragEnd = useCallback(
    (result, cardData) => {
      // Your drag end logic here
      onDragEnd(result, cardData);
    },
    [onDragEnd]
  );

  // Memoize the cards to prevent unnecessary re-renders
  const memoizedCards = useMemo(() => {
    return projects.map((project, index) => (
      <KanbanCard
        // status={name}
        boardName={boardName}
        key={`${boardId}-${project?.card?.card_id}`}
        id={project?.card?.card_id}
        project={project}
        onDragEnd={memoizedOnDragEnd}
        index={index}
        // callback={callback}
        isMyTaskCard={isMyTaskView}
        boardKey={boardName?.key}
        // assignedUser={project?.card?.assigned_to_user}
      />
    ));
  }, [projects, memoizedOnDragEnd, isMyTaskView, boardId, boardName]);

  // Scroll to newly created card functionality (for board view)
  useLayoutEffect(() => {
    if (scrollToCardId && scrollRef.current) {
      const cardElements = scrollRef.current.children;
      const cardIndex = projects.findIndex(
        (project) => project?.card?.card_id === scrollToCardId
      );

      if (cardIndex !== -1) {
        const targetCard = cardElements[cardIndex];
        if (targetCard) {
          const offsetTop = targetCard.offsetTop;
          scrollRef.current.scrollTo({
            top: offsetTop - 250,
            behavior: "smooth",
          });
        }
      }
    }
  }, [scrollToCardId, projects]);


  return (
    <div className="card-column" key={`column-${boardId}-${id}`}>
      <div className="coloumn-header">
        <h4 className="coloumn-header-h4">
          <p className="coloumn-header-text">{name}</p>
          <Tooltip title={TooltipName.TICKETS_IN_COLUMN}>
            <span className="coloumn-header-length">{projects.length}</span>
          </Tooltip>
        </h4>
      </div>
      <div
        className={
          isMyTaskView
            ? `kanban-column coloumn-body${
                maxHeight > 0 && maxHeight < 4 ? `-${maxHeight}` : ""
              }`
            : `coloumn-body ${
                mouseIsHovering ? "kanban-column-bg-1" : "kanban-column-bg-2"
              }`
        }
        onDragEnter={(e) => {
          setMouseIsHovering(true);
          onDragEnter && onDragEnter(e, stage);
        }}
        onDragLeave={(e) => setMouseIsHovering(false)}
        onDragOver={preventing}
      >
        <Droppable
          droppableId={id.toString()}
          key={`droppable-${boardId}-${id}`}
        >
          {(provided) => (
            <div
              className={
                isMyTaskView
                  ? `coloumn-all-cards-task-page${
                      maxHeight > 0 && maxHeight < 4 ? `-${maxHeight}` : ""
                    }`
                  : "coloumn-all-cards"
              }
              style={
                !isMyTaskView
                  ? {
                      height: `calc(94vh - ${milestone ? "340px" : "270px"})`,
                    }
                  : {}
              }
              ref={(node) => {
                provided.innerRef(node);
                !isMyTaskView && (scrollRef.current = node);
              }}
              {...provided.droppableProps}
            >
              {!isMyTaskView && (
                <CreateCard
                  stage={stage}
                  newCreatedCardId={setScrollToCardId}
                  key={`create-card-${boardId}-${id}`}
                  boardName={boardName}
                />
              )}
              {memoizedCards}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );
}

// Custom comparison function for memo
function areEqual(prevProps, nextProps) {
  return (
    prevProps.boardId === nextProps.boardId &&
    prevProps.id === nextProps.id &&
    prevProps.name === nextProps.name &&
    prevProps.projects === nextProps.projects &&
    prevProps.boardName === nextProps.boardName &&
    prevProps.isMyTaskView === nextProps.isMyTaskView &&
    prevProps.maxHeight === nextProps.maxHeight
  );
}

export default React.memo(KanbanColumn, areEqual);
